/**
 * Pure date + axis math for the Timeline (Gantt) view.
 *
 * All day arithmetic runs in a UTC "day-index" space (integer days since 1970-01-01,
 * computed from the calendar Y/M/D) so it is immune to the local timezone and DST — the
 * same trick DatePicker/format use to avoid tz drift on date-only ("YYYY-MM-DD") values.
 */

export const DAY_MS = 86400000;

export type Zoom = 'days' | 'weeks' | 'months';

export interface ZoomDef {
  id: Zoom;
  label: string;
  /** Horizontal pixels per calendar day at this zoom. */
  pxPerDay: number;
}

/** Discrete zoom levels, coarse → fine reading top-down in the picker. */
export const ZOOMS: ZoomDef[] = [
  { id: 'days', label: 'Days', pxPerDay: 40 },
  { id: 'weeks', label: 'Weeks', pxPerDay: 24 },
  { id: 'months', label: 'Months', pxPerDay: 8 },
];

export const zoomDef = (z: Zoom): ZoomDef => ZOOMS.find((x) => x.id === z) ?? ZOOMS[1];

const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "YYYY-MM-DD" → integer UTC day index (tz/DST-safe), or null when unparseable. */
export const dayFromISO = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

/** Integer UTC day index → "YYYY-MM-DD". */
export const isoFromDay = (n: number): string => {
  const dt = new Date(n * DAY_MS);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

/** Today's local calendar date as a UTC day index (so it lines up with parsed item dates). */
export const todayDay = (): number => {
  const n = new Date();
  return Math.round(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()) / DAY_MS);
};

const utc = (n: number) => new Date(n * DAY_MS);
const dow = (n: number) => utc(n).getUTCDay(); // 0=Sun … 6=Sat
export const isWeekendDay = (n: number): boolean => dow(n) === 0 || dow(n) === 6;

const alignMonday = (n: number) => n - ((dow(n) + 6) % 7); // back to the Monday of n's week
const firstOfMonth = (n: number) => {
  const d = utc(n);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / DAY_MS);
};
const nextMonth = (n: number) => {
  const d = utc(n);
  return Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1) / DAY_MS);
};
const nextYear = (n: number) => {
  const d = utc(n);
  return Math.round(Date.UTC(d.getUTCFullYear() + 1, 0, 1) / DAY_MS);
};

/** A shaded background column band (alternating weeks, or months at the Months zoom). */
export interface Band {
  x: number;
  w: number;
  shaded: boolean;
}
/** A bottom-tier tick label (day number / week-start day / short month). */
export interface Tick {
  x: number;
  label: string;
  dim?: boolean;
}
/** A top-tier span label (month name for days/weeks; year for months). */
export interface Span {
  x: number;
  w: number;
  label: string;
}

export interface Axis {
  /** Aligned first day of the canvas (Monday, or 1st of month at Months zoom). */
  start: number;
  /** Aligned day just past the last visible day (exclusive). */
  end: number;
  pxPerDay: number;
  /** Total canvas width in px. */
  width: number;
  bands: Band[];
  top: Span[];
  bottom: Tick[];
}

/**
 * Build the axis for [minDay, maxDay] at the given zoom. The range is snapped outward to
 * whole weeks (days/weeks) or whole months (months) so the grid columns and shading are
 * always complete at both ends.
 */
export function buildAxis(minDay: number, maxDay: number, zoom: Zoom): Axis {
  const px = zoomDef(zoom).pxPerDay;
  const monthly = zoom === 'months';

  const start = monthly ? firstOfMonth(minDay) : alignMonday(minDay);
  const end = monthly ? nextMonth(maxDay) : alignMonday(maxDay) + 7;
  const width = (end - start) * px;
  const X = (day: number) => (day - start) * px;

  // ---- shaded bands (alternating) ----
  const bands: Band[] = [];
  if (monthly) {
    let i = 0;
    for (let seg = start; seg < end; seg = nextMonth(seg), i++) {
      const segEnd = Math.min(nextMonth(seg), end);
      bands.push({ x: X(seg), w: (segEnd - seg) * px, shaded: i % 2 === 1 });
    }
  } else {
    let i = 0;
    for (let seg = start; seg < end; seg += 7, i++) {
      bands.push({ x: X(seg), w: 7 * px, shaded: i % 2 === 1 });
    }
  }

  // ---- top tier: month spans (days/weeks) or year spans (months) ----
  const top: Span[] = [];
  if (monthly) {
    for (let seg = start; seg < end; ) {
      const segEnd = Math.min(nextYear(seg), end);
      top.push({ x: X(seg), w: (segEnd - seg) * px, label: String(utc(seg).getUTCFullYear()) });
      seg = nextYear(seg);
    }
  } else {
    for (let seg = start; seg < end; ) {
      const segEnd = Math.min(nextMonth(seg), end);
      const d = utc(seg);
      const isFirst = top.length === 0;
      const label = d.getUTCMonth() === 0 || isFirst ? `${MONTHS_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}` : MONTHS_FULL[d.getUTCMonth()];
      top.push({ x: X(seg), w: (segEnd - seg) * px, label });
      seg = nextMonth(seg);
    }
  }

  // ---- bottom tier: day numbers / week-start day numbers / short month names ----
  const bottom: Tick[] = [];
  if (zoom === 'days') {
    for (let n = start; n < end; n++) bottom.push({ x: X(n), label: String(utc(n).getUTCDate()), dim: isWeekendDay(n) });
  } else if (zoom === 'weeks') {
    for (let n = start; n < end; n += 7) bottom.push({ x: X(n), label: String(utc(n).getUTCDate()) });
  } else {
    for (let seg = start; seg < end; seg = nextMonth(seg)) bottom.push({ x: X(seg), label: MONTHS_SHORT[utc(seg).getUTCMonth()] });
  }

  return { start, end, pxPerDay: px, width, bands, top, bottom };
}

/** A single, well-formed day span for an item (either date may be absent). */
export interface ItemSpan {
  s: number; // first day (min of the two)
  e: number; // last day (max of the two), inclusive
  hasStart: boolean;
  hasEnd: boolean;
}

/** Resolve an item's start/end into a day span, or null when it has no dates at all. */
export const itemSpan = (startISO: string | null, endISO: string | null): ItemSpan | null => {
  const sd = dayFromISO(startISO);
  const ed = dayFromISO(endISO);
  if (sd == null && ed == null) return null;
  const a = sd ?? (ed as number);
  const b = ed ?? (sd as number);
  return { s: Math.min(a, b), e: Math.max(a, b), hasStart: sd != null, hasEnd: ed != null };
};

/** How far back a "due date only" item's bar is pulled so it reads as a filled bar
 *  rather than a dot (bounded, and clamped to the canvas origin by {@link fillSpan}). */
export const FILL_LOOKBACK = 60;

/**
 * Give an end-only item a visible, filled bar by treating a distant past day as its start —
 * bounded to {@link FILL_LOOKBACK} days and never earlier than the canvas origin, so it stays
 * on-canvas and never stretches the axis. `hasStart` stays false: the synthetic start is purely
 * visual and is never persisted (a move rewrites only the real end date). Ranged and start-only
 * spans pass through unchanged.
 */
export const fillSpan = (span: ItemSpan, axisStart: number): ItemSpan =>
  span.hasEnd && !span.hasStart
    ? { s: Math.max(axisStart, span.e - FILL_LOOKBACK), e: span.e, hasStart: false, hasEnd: true }
    : span;
